import { useTranslation } from "@nova/use-translation"
import { useAppSelector } from "integrations/redux/hooks"
import { selectIsDomainWhiteListed } from "user/profile/selectors"
import { LicenseCapacities } from "./module"
import { Licenses } from "@nova/constants"
import { formatBytes } from "@nova/format"
import { License } from "@nova/schemas"

export function UsageBar<T extends Pick<License, "usageAmount" | "subscriptionType">>({
    license,
    additionalUsage = 0,
    theme = "dark",
    isSelf = true,
}: {
    license: T
    additionalUsage?: number
    isSelf?: boolean
    theme?: "light" | "dark"
}) {
    const { t } = useTranslation()
    const isWhitelisted = useAppSelector(selectIsDomainWhiteListed)
    const { usageAmount, subscriptionType } = license
    const usage = usageAmount || 0
    if (!(Object.values(Licenses) as string[]).includes(subscriptionType)) return null

    const totalUsage = usage + additionalUsage

    const usageInGB = usage / 1024 ** 3

    const capacity =
        subscriptionType === Licenses.Pro
            ? "∞"
            : `${LicenseCapacities[subscriptionType as Licenses].toString()} GB`

    const currentPercentage =
        subscriptionType !== Licenses.Pro
            ? Math.min(
                  Math.max((usageInGB / LicenseCapacities[subscriptionType as Licenses]) * 100, 0),
                  100
              )
            : undefined

    const newPercentage =
        subscriptionType !== Licenses.Pro
            ? Math.min(
                  Math.max(
                      (totalUsage / 1024 ** 3 / LicenseCapacities[subscriptionType as Licenses]) *
                          100,
                      0
                  ),
                  100
              )
            : undefined

    const isInfinite = subscriptionType === Licenses.Pro || (isWhitelisted && isSelf)
    return (
        <div>
            {!isInfinite && (
                <div
                    className="relative mb-1 h-1 w-full"
                    style={{
                        backgroundColor: theme === "dark" ? "#28282C" : "#404044", //gray-700, gray-500
                        borderRadius: "0.25rem",
                        overflow: "hidden",
                    }}
                    aria-label="usage-bar"
                >
                    <div
                        className="absolute top-0 z-10 h-full"
                        style={{
                            width: `${currentPercentage}%`,
                            backgroundColor: "#FFFFFFCC", //white-600
                        }}
                    />
                    <div
                        className="absolute top-0 h-full"
                        style={{
                            width: `${newPercentage}%`,
                            backgroundColor: "#FFFFFF33", //white-300
                        }}
                    />
                </div>
            )}
            {!additionalUsage ? (
                <small className="text-gray-300 mono">
                    {t(`${formatBytes(usage)} ${isInfinite ? "of ∞" : `of ${capacity}`}`)}
                </small>
            ) : (
                <div className="text-gray-300 small">
                    {t(
                        `Estimated ${formatBytes(totalUsage)} ${isInfinite ? `` : `of ${capacity}`} of data usage after upload`
                    )}
                </div>
            )}
        </div>
    )
}
